import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Field } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  createVendingProduct,
  patchVendingProduct,
  uploadVendingImage,
  type CreateVendingProductBody,
} from '../../lib/api';
import { PS, T } from '../../lib/theme';
import { TOURNAMENT_LOCALES, LOCALE_LABELS } from '../../lib/tournaments';
import { diffProduct, formatTetri, gelToTetri, validateSlug } from '../../lib/vending';
import type { AdminVendingProduct, VendingLocale, VendingProductDraft } from '../../lib/types';

interface Props {
  open: boolean;
  /** null makes this the create form; a row switches it into immutable-id edit mode. */
  product: AdminVendingProduct | null;
  nextDisplayOrder: number;
  onClose: () => void;
  onSaved: () => void;
}

type TranslationInputs = Record<VendingLocale, { name: string; description: string }>;

function emptyTranslations(product: AdminVendingProduct | null): TranslationInputs {
  return {
    en: {
      name: product?.translations.en?.name ?? '',
      description: product?.translations.en?.description ?? '',
    },
    ru: {
      name: product?.translations.ru?.name ?? '',
      description: product?.translations.ru?.description ?? '',
    },
    ka: {
      name: product?.translations.ka?.name ?? '',
      description: product?.translations.ka?.description ?? '',
    },
  };
}

function productDraft(
  id: string,
  priceMinor: number,
  displayOrder: number,
  isActive: boolean,
  translations: TranslationInputs,
): VendingProductDraft {
  return { id: id.trim(), price_minor: priceMinor, display_order: displayOrder, is_active: isActive, translations };
}

export function VendingProductDialog({ open, product, nextDisplayOrder, onClose, onSaved }: Props) {
  const { push } = useToast();
  const [id, setId] = useState('');
  const [price, setPrice] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [displayOrder, setDisplayOrder] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [translations, setTranslations] = useState<TranslationInputs>(() => emptyTranslations(null));
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const idRef = useRef<HTMLInputElement>(null);
  const initializedFormKeyRef = useRef<string | null | undefined>(undefined);
  const displayOrderTouchedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedFormKeyRef.current = undefined;
      displayOrderTouchedRef.current = false;
      return;
    }
    const formKey = product?.id ?? null;
    if (initializedFormKeyRef.current === formKey) {
      if (!product && !displayOrderTouchedRef.current) {
        setDisplayOrder(String(nextDisplayOrder));
      }
      return;
    }
    initializedFormKeyRef.current = formKey;
    displayOrderTouchedRef.current = false;
    setId(product?.id ?? '');
    setImageUrl(product?.image_url ?? '');
    setImageFile(null);
    if (fileRef.current) fileRef.current.value = '';
    setPrice(product ? formatTetri(product.price_minor) : '');
    setDisplayOrder(String(product?.display_order ?? nextDisplayOrder));
    setIsActive(product?.is_active ?? true);
    setTranslations(emptyTranslations(product));
    setBusy(false);
    setInlineError(null);
    setArchiveConfirmOpen(false);
  }, [open, product, nextDisplayOrder]);

  useEffect(() => {
    if (open && !product) idRef.current?.focus();
  }, [open, product]);

  useEffect(() => {
    if (!open || !imageFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [open, imageFile]);

  const priceMinor = gelToTetri(price);
  const order = Number(displayOrder);
  const slugError = validateSlug(id.trim());
  const hasName = TOURNAMENT_LOCALES.some((locale) => translations[locale].name.trim().length > 0);
  const formValid =
    slugError === null &&
    priceMinor !== null &&
    priceMinor >= 1 &&
    displayOrder.trim() !== '' &&
    Number.isInteger(order) &&
    hasName;
  const draft = useMemo(
    () => ({ ...productDraft(id, priceMinor ?? -1, order, isActive, translations), image_url: imageUrl.trim() || null }),
    [id, isActive, order, priceMinor, translations, imageUrl],
  );
  const patch = product && formValid ? diffProduct(product, draft) : null;
  const canSubmit = !busy && formValid && (!product || patch !== null || imageFile !== null);

  const updateTranslation = (locale: VendingLocale, field: 'name' | 'description', value: string) => {
    setTranslations((current) => ({
      ...current,
      [locale]: { ...current[locale], [field]: value },
    }));
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || priceMinor === null) return;
    setBusy(true);
    setInlineError(null);
    try {
      let savedImageUrl = imageUrl.trim() || null;
      if (imageFile) {
        setUploading(true);
        const uploaded = await uploadVendingImage(imageFile);
        savedImageUrl = uploaded.image_url;
        setImageUrl(uploaded.image_url);
        setImageFile(null);
        if (fileRef.current) fileRef.current.value = '';
        setUploading(false);
      }
      if (product) {
        // `canSubmit` above ensures this cannot be the server's `no_fields` body.
        await patchVendingProduct(product.id, { ...patch, ...(savedImageUrl !== (product.image_url ?? null) ? { image_url: savedImageUrl } : {}) });
        push('success', 'Product updated.');
      } else {
        const createTranslations: CreateVendingProductBody['translations'] = {};
        for (const locale of TOURNAMENT_LOCALES) {
          const translation = translations[locale];
          const name = translation.name.trim();
          if (name) {
            createTranslations[locale] = {
              name,
              description: translation.description.trim() || null,
            };
          }
        }
        const body: CreateVendingProductBody = {
          id: id.trim(),
          price_minor: priceMinor,
          display_order: order,
          is_active: isActive,
          translations: createTranslations,
        };
        if (savedImageUrl) body.image_url = savedImageUrl;
        await createVendingProduct(body);
        push('success', 'Product created.');
      }
      onClose();
      onSaved();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'product_exists') {
        setInlineError('A product with this id already exists');
      } else {
        setInlineError(error instanceof ApiError ? error.message : 'Could not save product.');
      }
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  const setArchived = async (active: boolean) => {
    if (!product) return;
    setBusy(true);
    setInlineError(null);
    try {
      await patchVendingProduct(product.id, { is_active: active });
      push('success', active ? 'Product restored.' : 'Product archived.');
      setArchiveConfirmOpen(false);
      onClose();
      onSaved();
    } catch (error) {
      // The confirmation layer would otherwise obscure this inline error.
      setArchiveConfirmOpen(false);
      setInlineError(error instanceof ApiError ? error.message : 'Could not update product status.');
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={() => { if (!busy) onClose(); }} title={product ? `Edit product: ${product.id}` : 'New product'} maxWidth={700}>
        <form onSubmit={(event) => void save(event)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {inlineError && (
            <div
              role="alert"
              style={{ padding: 10, border: `1px solid ${PS.errorBorder}`, borderRadius: 3, color: PS.errorText, background: PS.errorBg, ...T.ui }}
            >
              {inlineError}
            </div>
          )}

          <Field label="Id">
            <input
              ref={idRef}
              value={id}
              readOnly={Boolean(product)}
              onChange={(event) => setId(event.target.value)}
              aria-describedby="product-id-hint"
              style={{ ...inputStyle, background: product ? PS.surfaceDisabled : PS.white }}
            />
          </Field>
          <span id="product-id-hint" style={{ ...T.caption, color: slugError ? PS.errorText : PS.textSec, marginTop: -10 }}>
            a-z 0-9 _ · 2–32 characters{slugError && id ? ` · ${slugError}` : ''}
          </span>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Field label="Product image (optional)">
              <input
                ref={fileRef}
                type="file"
                aria-label="Choose image"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                disabled={busy}
                onChange={(event) => {
                  setImageFile(event.target.files?.[0] ?? null);
                  setInlineError(null);
                }}
                style={{ ...inputStyle, maxWidth: '100%', boxSizing: 'border-box' }}
              />
              <span style={{ ...T.caption, color: PS.textSec }}>Choose a photo from your device. JPEG, PNG, WebP or HEIC, up to 10 MB.</span>
            </Field>
            {(previewUrl || imageUrl) && <PhotoPreview key={previewUrl || imageUrl} src={previewUrl || imageUrl} />}
            {(imageFile || imageUrl) && (
              <Button type="button" variant="secondary" disabled={busy} onClick={() => {
                setImageFile(null);
                setImageUrl('');
                if (fileRef.current) fileRef.current.value = '';
              }}>Remove image</Button>
            )}
          </div>

          {TOURNAMENT_LOCALES.map((locale) => (
            <fieldset key={locale} style={fieldsetStyle}>
              <legend style={{ ...T.subheading, color: PS.nearBlack, padding: '0 4px' }}>{LOCALE_LABELS[locale]}</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Field label="Name">
                  <input
                    value={translations[locale].name}
                    onChange={(event) => updateTranslation(locale, 'name', event.target.value)}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Description">
                  <textarea
                    value={translations[locale].description}
                    onChange={(event) => updateTranslation(locale, 'description', event.target.value)}
                    rows={2}
                    style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                  />
                </Field>
              </div>
            </fieldset>
          ))}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Price (₾)">
              <input
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                inputMode="decimal"
                aria-invalid={price !== '' && (priceMinor === null || priceMinor < 1)}
                style={inputStyle}
              />
            </Field>
            <Field label="Display order">
              <input
                type="number"
                min="0"
                step="1"
                value={displayOrder}
                onChange={(event) => {
                  displayOrderTouchedRef.current = true;
                  setDisplayOrder(event.target.value);
                }}
                style={inputStyle}
              />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...T.ui }}>
            <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
            Active
          </label>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div>
              {product && (product.is_active ? (
                <Button type="button" variant="danger" onClick={() => setArchiveConfirmOpen(true)} disabled={busy}>Archive</Button>
              ) : (
                <Button type="button" variant="secondary" onClick={() => void setArchived(true)} disabled={busy}>Restore</Button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={!canSubmit}>{uploading ? 'Uploading…' : busy ? 'Saving…' : product ? 'Save changes' : 'Create product'}</Button>
            </div>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={archiveConfirmOpen}
        title="Archive product?"
        message="Archived products disappear from the customer menu; past purchases keep them."
        confirmLabel="Archive"
        destructive
        onConfirm={() => setArchived(false)}
        onCancel={() => setArchiveConfirmOpen(false)}
      />
    </>
  );
}

function PhotoPreview({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <span style={{ ...T.caption, color: PS.textSec }}>Photo selected. Preview will appear after saving.</span>
  ) : (
    <img src={src} alt="Product preview" onError={() => setFailed(true)}
      style={{ width: 120, height: 120, objectFit: 'contain', borderRadius: 3 }} />
  );
}

const inputStyle = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  color: PS.nearBlack,
  ...T.ui,
};

const fieldsetStyle = {
  margin: 0,
  padding: 12,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 3,
};
